import { useCallback, useEffect, useState } from 'react';

import { toast } from 'react-toastify';

import { socket } from '../../libs/socket';

import type { PermissionState } from './types';

type PermissionStatusWithOnChange = PermissionStatus & {
  onchange: (() => void) | null;
};

type NavigatorWithPermissions = Navigator & {
  permissions?: {
    query?: (descriptor: { name: PermissionName }) => Promise<PermissionStatusWithOnChange>;
  };
};

interface UseMeetPermissionsArgs {
  roomName: string;
  audioDevice?: MediaDeviceInfo;
  videoDevice?: MediaDeviceInfo;
  toggleAudio?: () => boolean | void;
  toggleVideo?: () => boolean | void | Promise<boolean | void>;
}

export function useMeetPermissions({
  roomName,
  audioDevice,
  videoDevice,
  toggleAudio,
  toggleVideo,
}: UseMeetPermissionsArgs) {
  const [isAudioMuted, setIsAudioMuted] = useState(false);
  const [isVideoMuted, setIsVideoMuted] = useState(false);
  const [micPermission, setMicPermission] = useState<PermissionState>('unknown');
  const [camPermission, setCamPermission] = useState<PermissionState>('unknown');
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);

  const inferPermissionsFromDevices = useCallback(async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
        return;
      }

      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = devices.filter((device) => device.kind === 'audioinput');
      const videoInputs = devices.filter((device) => device.kind === 'videoinput');

      if (audioInputs.some((device) => Boolean(device.label?.trim().length))) {
        setMicPermission('granted');
      }

      if (videoInputs.some((device) => Boolean(device.label?.trim().length))) {
        setCamPermission('granted');
      }
    } catch {
      // ignore
    }
  }, []);

  const refreshPermissions = useCallback(async () => {
    const permissionsApi = (navigator as NavigatorWithPermissions).permissions;

    if (permissionsApi && typeof permissionsApi.query === 'function') {
      try {
        const mic = await permissionsApi.query({ name: 'microphone' as PermissionName });
        setMicPermission(mic.state as PermissionState);

        const cam = await permissionsApi.query({ name: 'camera' as PermissionName });
        setCamPermission(cam.state as PermissionState);
      } catch {
        // ignore
      }
    }

    await inferPermissionsFromDevices();
  }, [inferPermissionsFromDevices]);

  const reRequestPermissions = useCallback(async () => {
    try {
      const videoConstraint: MediaTrackConstraints | boolean = videoDevice?.deviceId
        ? { deviceId: { exact: videoDevice.deviceId } }
        : true;
      const audioConstraint: MediaTrackConstraints | boolean = audioDevice?.deviceId
        ? { deviceId: { exact: audioDevice.deviceId } }
        : true;

      const stream = await navigator.mediaDevices.getUserMedia({
        video: videoConstraint,
        audio: audioConstraint,
      });

      stream.getTracks().forEach((track) => track.stop());
      await refreshPermissions();
      toast.success('Доступ к камере и микрофону предоставлен');
    } catch (error) {
      console.error('Повторный запрос доступа отклонён/не удался', error);
      toast.error('Не удалось получить доступ. Проверьте настройки браузера и сайта.');
    }
  }, [audioDevice, refreshPermissions, videoDevice]);

  const toggleMuteVideo = useCallback(async () => {
    if (camPermission !== 'granted') {
      setShowPermissionsModal(true);
      void reRequestPermissions();
      return;
    }

    try {
      const nextMutedState = await toggleVideo?.();

      if (typeof nextMutedState !== 'boolean') {
        return;
      }

      setIsVideoMuted(nextMutedState);

      socket.emit('message', {
        roomId: roomName,
        message: {
          datetime: new Date().toISOString(),
          action: 'video_client',
          data: !nextMutedState,
        },
      });
    } catch {
      // LiveKit keeps the previous state and reports the media error.
    }
  }, [camPermission, reRequestPermissions, roomName, toggleVideo]);

  const toggleMuteAudio = useCallback(() => {
    if (micPermission !== 'granted') {
      setShowPermissionsModal(true);
      void reRequestPermissions();
      return;
    }

    const nextMutedState = toggleAudio?.();
    setIsAudioMuted(nextMutedState ?? false);

    socket.emit('message', {
      roomId: roomName,
      message: {
        datetime: new Date().toISOString(),
        action: 'micro_client',
        data: !(nextMutedState ?? false),
      },
    });
  }, [micPermission, reRequestPermissions, roomName, toggleAudio]);

  useEffect(() => {
    const permissionsApi = (navigator as NavigatorWithPermissions).permissions;

    if (!permissionsApi || typeof permissionsApi.query !== 'function') {
      return;
    }

    try {
      permissionsApi
        .query({ name: 'microphone' as PermissionName })
        .then((status) => {
          setMicPermission(status.state as PermissionState);
          status.onchange = () => setMicPermission(status.state as PermissionState);
        })
        .catch(() => undefined);

      permissionsApi
        .query({ name: 'camera' as PermissionName })
        .then((status) => {
          setCamPermission(status.state as PermissionState);
          status.onchange = () => setCamPermission(status.state as PermissionState);
        })
        .catch(() => undefined);
    } catch {
      // ignore
    }

    void inferPermissionsFromDevices();
  }, [inferPermissionsFromDevices]);

  useEffect(() => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.addEventListener) {
      return;
    }

    const handleDeviceChange = () => {
      void inferPermissionsFromDevices();
    };

    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);

    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
    };
  }, [inferPermissionsFromDevices]);

  return {
    isAudioMuted,
    isVideoMuted,
    micPermission,
    camPermission,
    showPermissionsModal,
    setShowPermissionsModal,
    inferPermissionsFromDevices,
    refreshPermissions,
    reRequestPermissions,
    toggleMuteAudio,
    toggleMuteVideo,
    shouldShowAudioRed: isAudioMuted || (micPermission !== 'granted' && micPermission !== 'unknown'),
    shouldShowVideoRed: isVideoMuted || (camPermission !== 'granted' && camPermission !== 'unknown'),
    showMicWarning: micPermission !== 'granted',
    showCamWarning: camPermission !== 'granted',
    setVideoMuted: setIsVideoMuted,
    handleMutedByHost: () => {
      setIsAudioMuted(true);
      toast.warning('Психолог выключил ваш микрофон');
    },
    handleVideoStoppedByHost: () => {
      setIsVideoMuted(true);
      toast.warning('Психолог выключил вашу камеру');
    },
  };
}
